/**
 * Sprint · Feb 2026 · Iter-7 — Product Catalog Picker (with configurator).
 *
 * Modal usado desde Create Estimate → Add Product → "Productos Personalizados".
 * Dos vistas internas:
 *   1) Listado de productos activos del Product Pricing Catalog.
 *   2) Configurador del producto elegido: Tipo · Cantidad · Extras + resumen
 *      cost/price + botón "Agregar al Estimate".
 *
 * Fuentes de datos (aditivas, sólo GETs existentes):
 *   - `productCatalogApi.list()`     → pricing_products (types/quantities/extras embebidos).
 *   - `productCatalogApi.getMatrix(id)` → pricing_matrix (cost/price por type × quantity).
 *
 * Cálculo aprobado por el usuario:
 *   base_cost    = cell.cost
 *   base_price   = cell.price
 *   extras_cost  = Σ extra.cost seleccionados
 *   extras_price = Σ extra.price seleccionados
 *   total_cost   = base_cost + extras_cost
 *   total_price  = base_price + extras_price
 *
 * Se emite UN solo item vía `onAdd` con `quantity = 1` (el precio corresponde
 * al lote completo). `product_catalog_ref` conserva ids + `quantity_value` real.
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { productCatalogApi } from "./api";
import { useAuth } from "@/context/AuthContext";

const money = (v) =>
  `$${Number(v || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

// Agrupación visual por `category` (campo existente, sin backend nuevo).
// Mismo criterio visual que ProductCatalogListPage.
const groupProducts = (list) => {
  const map = new Map();
  for (const p of list) {
    const key = (p.category || "").trim() || "Sin categoría";
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(p);
  }
  return Array.from(map.entries()).sort((a, b) => {
    if (a[0] === "Sin categoría") return 1;
    if (b[0] === "Sin categoría") return -1;
    return a[0].localeCompare(b[0]);
  });
};

export default function ProductCatalogPickerModal({ open, onClose, onAdd }) {
  const { isAdmin } = useAuth();
  const canAddToEstimate = typeof onAdd === "function";

  // ---------- Listado ----------
  const [products, setProducts] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [search, setSearch] = useState("");

  // Búsqueda client-side (mismo criterio que ProductCatalogListPage):
  // nombre + categoría + nombres de tipos, insensible a mayúsculas,
  // acentos y espacios ("14pt" encuentra "Standard 14 pt").
  const norm = (s) =>
    String(s || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();
  const q = norm(search.trim());
  const qc = q.replace(/\s+/g, "");
  const hit = (v) => {
    const n = norm(v);
    return n.includes(q) || n.replace(/\s+/g, "").includes(qc);
  };
  const visibleProducts = !q
    ? products
    : products.filter(
        (p) =>
          hit(p.name) ||
          hit(p.category) ||
          (p.types || []).some((t) => hit(t.name))
      );

  // ---------- Configurador ----------
  const [selected, setSelected] = useState(null); // producto completo
  const [matrix, setMatrix] = useState([]);
  const [loadingCfg, setLoadingCfg] = useState(false);
  const [typeId, setTypeId] = useState(null);
  const [qtyId, setQtyId] = useState(null);
  const [qtyInput, setQtyInput] = useState(""); // modo "range": cantidad libre
  const [extraIds, setExtraIds] = useState(() => new Set());

  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoadingList(true);
      try {
        const list = await productCatalogApi.list();
        setProducts((list || []).filter((p) => p.active !== false));
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Error al cargar catálogo");
      } finally {
        setLoadingList(false);
      }
    })();
  }, [open]);

  // Reset al cerrar
  useEffect(() => {
    if (!open) {
      setSelected(null);
      setMatrix([]);
      setTypeId(null);
      setQtyId(null);
      setQtyInput("");
      setExtraIds(new Set());
    }
  }, [open]);

  const openProduct = async (product) => {
    setSelected(product);
    setTypeId(null);
    setQtyId(null);
    setQtyInput("");
    setExtraIds(new Set());
    setLoadingCfg(true);
    try {
      const cells = await productCatalogApi.getMatrix(product.id);
      setMatrix(cells || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al cargar precios");
      setMatrix([]);
    } finally {
      setLoadingCfg(false);
    }
  };

  const activeTypes = useMemo(
    () => (selected?.types || []).filter((t) => t.active !== false).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)),
    [selected]
  );
  const activeQuantities = useMemo(
    () => (selected?.quantities || []).filter((q) => q.active !== false).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)),
    [selected]
  );
  const activeExtras = useMemo(
    () => (selected?.extras || []).filter((e) => e.active !== false).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)),
    [selected]
  );

  // ---- Modo rango (Jun 2026): cantidad libre → rango → precio UNITARIO ----
  const isRange = selected?.quantity_mode === "range";
  const qtyNum = isRange ? parseInt(qtyInput, 10) || 0 : 0;
  const rangeRow = useMemo(() => {
    if (!isRange || qtyNum <= 0) return null;
    return (
      activeQuantities.find(
        (q) => qtyNum >= q.quantity && (q.max_qty == null || qtyNum <= q.max_qty)
      ) || null
    );
  }, [isRange, qtyNum, activeQuantities]);
  const rangeLabel = (q) => `${q.quantity}${q.max_qty != null ? `–${q.max_qty}` : "+"}`;

  const effectiveQtyId = isRange ? rangeRow?.id || null : qtyId;
  const cell = useMemo(() => {
    if (!typeId || !effectiveQtyId) return null;
    return matrix.find((c) => c.type_id === typeId && c.quantity_id === effectiveQtyId) || null;
  }, [matrix, typeId, effectiveQtyId]);

  const selectedExtras = useMemo(
    () => activeExtras.filter((e) => extraIds.has(e.id)),
    [activeExtras, extraIds]
  );
  const perUnitExtras = selectedExtras.filter((e) => e.charge_mode === "per_unit");
  const flatExtras = selectedExtras.filter((e) => e.charge_mode !== "per_unit");

  // Unidades del lote: en modo rango la cantidad escrita; en package el tier.
  const pkgQtyValue = !isRange
    ? activeQuantities.find((q) => q.id === qtyId)?.quantity || 0
    : 0;
  const units = isRange ? qtyNum : pkgQtyValue;

  const baseCost = cell ? Number(cell.cost || 0) : 0;
  const basePrice = cell ? Number(cell.price || 0) : 0;
  const perUnitExtrasCost = perUnitExtras.reduce((acc, e) => acc + Number(e.cost || 0), 0);
  const perUnitExtrasPrice = perUnitExtras.reduce((acc, e) => acc + Number(e.price || 0), 0);
  const flatExtrasCost = flatExtras.reduce((acc, e) => acc + Number(e.cost || 0), 0);
  const flatExtrasPrice = flatExtras.reduce((acc, e) => acc + Number(e.price || 0), 0);

  // Package (histórico): todo en una línea qty=1. per_unit multiplica por el tier.
  const extrasCost = flatExtrasCost + perUnitExtrasCost * (isRange ? 0 : units);
  const extrasPrice = flatExtrasPrice + perUnitExtrasPrice * (isRange ? 0 : units);
  const totalCost = isRange
    ? (baseCost + perUnitExtrasCost) * qtyNum + flatExtrasCost
    : baseCost + extrasCost;
  const totalPrice = isRange
    ? (basePrice + perUnitExtrasPrice) * qtyNum + flatExtrasPrice
    : basePrice + extrasPrice;
  const rangeUnitPrice = basePrice + perUnitExtrasPrice;

  const toggleExtra = (id) => {
    setExtraIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleAdd = () => {
    if (!canAddToEstimate) return;
    if (isRange) {
      if (!selected || !typeId || qtyNum <= 0 || !rangeRow || !cell) {
        toast.error(
          !rangeRow && qtyNum > 0
            ? "La cantidad no cae en ningún rango configurado"
            : "Seleccioná Tipo y escribí una Cantidad"
        );
        return;
      }
      const typeName = activeTypes.find((t) => t.id === typeId)?.name || "";
      const perUnitNames = perUnitExtras.map((e) => e.name);
      const desc = [
        selected.name,
        typeName,
        `Rango ${rangeLabel(rangeRow)}`,
        perUnitNames.length ? `Extras: ${perUnitNames.join(", ")}` : null,
      ]
        .filter(Boolean)
        .join(" · ");
      onAdd({
        product_code: `PC:${selected.id}`,
        description: desc,
        quantity: qtyNum,
        unit_price: rangeUnitPrice,
        unit_cost: baseCost + perUnitExtrasCost,
        notes: "",
        source_type: "product_catalog",
        product_catalog_ref: {
          product_id: selected.id,
          type_id: typeId,
          quantity_id: rangeRow.id,
          quantity_value: qtyNum,
          extra_ids: perUnitExtras.map((e) => e.id),
        },
      });
      if (flatExtras.length) {
        onAdd({
          product_code: `PC:${selected.id}`,
          description: `${selected.name} · Extras: ${flatExtras.map((e) => e.name).join(", ")}`,
          quantity: 1,
          unit_price: flatExtrasPrice,
          unit_cost: flatExtrasCost,
          notes: "",
          source_type: "product_catalog",
          product_catalog_ref: {
            product_id: selected.id,
            type_id: typeId,
            quantity_id: rangeRow.id,
            quantity_value: qtyNum,
            extra_ids: flatExtras.map((e) => e.id),
          },
        });
      }
      toast.success("Producto agregado al Estimate");
      onClose();
      return;
    }
    if (!selected || !typeId || !qtyId || !cell) {
      toast.error("Seleccioná Tipo y Cantidad");
      return;
    }
    const typeName = activeTypes.find((t) => t.id === typeId)?.name || "";
    const qtyObj = activeQuantities.find((q) => q.id === qtyId);
    const qtyValue = qtyObj?.quantity || 0;
    const extrasNames = selectedExtras.map((e) => e.name);
    const desc = [
      selected.name,
      typeName,
      `${qtyValue} uds.`,
      extrasNames.length ? `Extras: ${extrasNames.join(", ")}` : null,
    ]
      .filter(Boolean)
      .join(" · ");

    const item = {
      product_code: `PC:${selected.id}`,
      description: desc,
      quantity: 1,
      unit_price: totalPrice,
      unit_cost: totalCost,
      notes: "",
      source_type: "product_catalog",
      product_catalog_ref: {
        product_id: selected.id,
        type_id: typeId,
        quantity_id: qtyId,
        quantity_value: qtyValue,
        extra_ids: Array.from(extraIds),
      },
    };
    onAdd(item);
    toast.success("Producto agregado al Estimate");
    onClose();
  };

  if (!open) return null;

  // ---------- Vista Configurador ----------
  if (selected) {
    return (
      <div
        data-testid="product-catalog-picker-overlay"
        onClick={onClose}
        className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      >
        <div
          data-testid="product-catalog-configurator"
          onClick={(e) => e.stopPropagation()}
          className="bg-white w-full max-w-4xl max-h-[92vh] overflow-auto border-2 border-black"
        >
          <div className="px-6 py-5 border-b-2 border-black flex items-start justify-between gap-3">
            <div>
              <button
                type="button"
                data-testid="pc-configurator-back"
                onClick={() => setSelected(null)}
                className="text-xs font-mono-tech underline text-[var(--text-secondary)] hover:text-black mb-1"
              >
                ← Volver al catálogo
              </button>
              <div className="label-overline">GP / CATÁLOGO INTERNO</div>
              <h2 className="font-display text-2xl sm:text-3xl font-black tracking-tight mt-1">
                {selected.name}
              </h2>
              <p className="text-xs font-mono-tech uppercase tracking-wider text-[var(--text-secondary)] mt-1">
                {selected.category || "Sin categoría"}
              </p>
            </div>
            <button
              type="button"
              data-testid="product-catalog-picker-close"
              onClick={onClose}
              className="font-mono-tech text-xs font-bold px-3 py-2 border border-black hover:bg-black hover:text-white"
            >
              ✕
            </button>
          </div>

          <div className="p-6 space-y-6">
            {loadingCfg ? (
              <div className="text-sm text-[var(--text-secondary)] py-10 text-center font-mono-tech">
                LOADING…
              </div>
            ) : (
              <>
                {/* TIPO */}
                <section data-testid="pc-cfg-types">
                  <div className="font-mono-tech text-[0.65rem] font-bold tracking-[0.2em] text-[var(--text-secondary)] mb-2">
                    TIPO
                  </div>
                  {activeTypes.length === 0 ? (
                    <div className="text-sm text-[var(--text-secondary)] italic">
                      Este producto todavía no tiene tipos configurados.
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {activeTypes.map((t) => {
                        const on = typeId === t.id;
                        return (
                          <button
                            key={t.id}
                            type="button"
                            data-testid={`pc-cfg-type-${t.id}`}
                            onClick={() => setTypeId(t.id)}
                            className={`px-3 py-2 border text-sm font-display font-bold ${
                              on
                                ? "bg-black text-white border-black"
                                : "bg-white text-black border-black hover:bg-[var(--bg)]"
                            }`}
                          >
                            {t.name}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </section>

                {/* CANTIDAD */}
                <section data-testid="pc-cfg-quantities">
                  <div className="font-mono-tech text-[0.65rem] font-bold tracking-[0.2em] text-[var(--text-secondary)] mb-2">
                    CANTIDAD
                  </div>
                  {activeQuantities.length === 0 ? (
                    <div className="text-sm text-[var(--text-secondary)] italic">
                      Este producto todavía no tiene cantidades configuradas.
                    </div>
                  ) : isRange ? (
                    <div className="space-y-2">
                      <input
                        data-testid="pc-cfg-qty-input"
                        type="number"
                        min="1"
                        value={qtyInput}
                        onChange={(e) => setQtyInput(e.target.value)}
                        placeholder="Escribí la cantidad…"
                        className="w-full sm:w-64 border border-black px-3 py-2 text-sm font-mono-tech focus:outline-none"
                      />
                      <div className="flex flex-wrap gap-2">
                        {activeQuantities.map((q) => {
                          const on = rangeRow?.id === q.id;
                          return (
                            <span
                              key={q.id}
                              data-testid={`pc-cfg-range-${q.id}`}
                              className={`px-3 py-1.5 border text-xs font-mono-tech font-bold ${
                                on
                                  ? "bg-black text-white border-black"
                                  : "bg-white text-[var(--text-secondary)] border-[var(--border)]"
                              }`}
                            >
                              {rangeLabel(q)}
                            </span>
                          );
                        })}
                      </div>
                      {qtyNum > 0 && !rangeRow && (
                        <div className="text-xs font-mono-tech text-[var(--accent-magenta,#E5007D)]">
                          La cantidad {qtyNum} no cae en ningún rango configurado.
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {activeQuantities.map((q) => {
                        const on = qtyId === q.id;
                        return (
                          <button
                            key={q.id}
                            type="button"
                            data-testid={`pc-cfg-qty-${q.id}`}
                            onClick={() => setQtyId(q.id)}
                            className={`px-3 py-2 border text-sm font-mono-tech font-bold ${
                              on
                                ? "bg-black text-white border-black"
                                : "bg-white text-black border-black hover:bg-[var(--bg)]"
                            }`}
                          >
                            {q.quantity}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </section>

                {/* EXTRAS */}
                <section data-testid="pc-cfg-extras">
                  <div className="font-mono-tech text-[0.65rem] font-bold tracking-[0.2em] text-[var(--text-secondary)] mb-2">
                    EXTRAS (OPCIONALES)
                  </div>
                  {activeExtras.length === 0 ? (
                    <div className="text-sm text-[var(--text-secondary)] italic">
                      Sin extras disponibles para este producto.
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {activeExtras.map((e) => {
                        const on = extraIds.has(e.id);
                        return (
                          <label
                            key={e.id}
                            data-testid={`pc-cfg-extra-${e.id}`}
                            className={`flex items-center gap-3 border px-3 py-2 cursor-pointer text-sm ${
                              on ? "bg-black text-white border-black" : "bg-white border-[var(--border)] hover:bg-[var(--bg)]"
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={on}
                              onChange={() => toggleExtra(e.id)}
                              className="w-4 h-4 accent-black"
                            />
                            <span className="flex-1 font-display font-bold truncate">{e.name}</span>
                            <span className="font-mono-tech text-xs opacity-80">
                              +{money(e.price)}
                              {e.charge_mode === "per_unit" ? " c/u" : ""}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </section>

                {/* RESUMEN */}
                <section
                  data-testid="pc-cfg-summary"
                  className="border-2 border-black p-4 bg-[var(--bg)]"
                >
                  <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm font-mono-tech">
                    {isRange && (
                      <>
                        <div>Cantidad</div>
                        <div data-testid="pc-summary-qty" className="text-right">{qtyNum || "—"}</div>
                        <div>Precio unitario{perUnitExtras.length ? " (+ extras c/u)" : ""}</div>
                        <div data-testid="pc-summary-unit-price" className="text-right">{money(rangeUnitPrice)}</div>
                      </>
                    )}
                    {isAdmin && (
                      <>
                        <div>Costo base</div>
                        <div data-testid="pc-summary-base-cost" className="text-right">{money(baseCost)}</div>
                      </>
                    )}
                    <div>Precio base</div>
                    <div data-testid="pc-summary-base-price" className="text-right">{money(basePrice)}</div>
                    {isAdmin && (
                      <>
                        <div>Costo extras</div>
                        <div data-testid="pc-summary-extras-cost" className="text-right">{money(extrasCost)}</div>
                      </>
                    )}
                    <div>Precio extras</div>
                    <div data-testid="pc-summary-extras-price" className="text-right">{money(extrasPrice)}</div>
                    {isAdmin && (
                      <>
                        <div className="border-t border-black mt-1 pt-1 font-display font-bold text-base">Costo total</div>
                        <div data-testid="pc-summary-total-cost" className="text-right border-t border-black mt-1 pt-1 font-display font-bold text-base">
                          {money(totalCost)}
                        </div>
                      </>
                    )}
                    <div className={`font-display font-bold text-base ${!isAdmin ? "border-t border-black mt-1 pt-1" : ""}`}>Precio total</div>
                    <div data-testid="pc-summary-total-price" className={`text-right font-display font-bold text-base ${!isAdmin ? "border-t border-black mt-1 pt-1" : ""}`}>
                      {money(totalPrice)}
                    </div>
                  </div>
                  {typeId && effectiveQtyId && !cell && (
                    <div className="mt-3 text-xs font-mono-tech text-[var(--accent-magenta,#E5007D)]">
                      No hay precio configurado para esta combinación.
                    </div>
                  )}
                </section>
              </>
            )}
          </div>

          <div className="px-6 py-4 border-t-2 border-black flex justify-between items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="text-sm font-mono-tech underline text-[var(--text-secondary)] hover:text-black"
            >
              Cancelar
            </button>
            {canAddToEstimate ? (
              <button
                type="button"
                data-testid="pc-cfg-add-to-estimate"
                onClick={handleAdd}
                disabled={!cell}
                className="bg-black text-white px-5 py-2 font-display font-bold text-sm disabled:opacity-40"
              >
                Agregar al Estimate · {money(totalPrice)}
              </button>
            ) : (
              <button
                type="button"
                onClick={onClose}
                className="bg-black text-white px-5 py-2 font-display font-bold text-sm"
              >
                Cerrar
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ---------- Vista Lista ----------
  return (
    <div
      data-testid="product-catalog-picker-overlay"
      onClick={onClose}
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
    >
      <div
        data-testid="product-catalog-picker-modal"
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-4xl max-h-[92vh] overflow-auto border-2 border-black"
      >
        <div className="px-6 py-5 border-b-2 border-black flex items-start justify-between gap-3">
          <div>
            <div className="label-overline">GP / CATÁLOGO INTERNO</div>
            <h2 className="font-display text-2xl sm:text-3xl font-black tracking-tight mt-1">
              Productos Personalizados
            </h2>
            <p className="text-sm text-[var(--text-secondary)] mt-1">
              {canAddToEstimate
                ? "Elegí un producto para configurarlo y agregarlo al Estimate."
                : "Consulta del catálogo interno."}
            </p>
          </div>
          <button
            type="button"
            data-testid="product-catalog-picker-close"
            onClick={onClose}
            className="font-mono-tech text-xs font-bold px-3 py-2 border border-black hover:bg-black hover:text-white"
          >
            ✕
          </button>
        </div>

        <div className="p-6">
          {loadingList ? (
            <div className="text-sm text-[var(--text-secondary)] py-10 text-center font-mono-tech">
              LOADING…
            </div>
          ) : products.length === 0 ? (
            <div className="border border-dashed border-[var(--border)] py-14 text-center text-[var(--text-secondary)]">
              No hay productos activos en el catálogo.
            </div>
          ) : (
            <div data-testid="product-catalog-picker-list" className="space-y-5">
              <input
                data-testid="pc-picker-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por nombre, categoría o tipo…"
                className="w-full border border-[var(--border)] bg-white px-4 py-2.5 text-sm focus:outline-none focus:border-black"
              />
              {visibleProducts.length === 0 ? (
                <div
                  data-testid="pc-picker-search-empty"
                  className="border border-dashed border-[var(--border)] py-12 text-center text-[var(--text-secondary)]"
                >
                  Sin resultados para &quot;{search.trim()}&quot;.
                </div>
              ) : (
              groupProducts(visibleProducts).map(([cat, items]) => (
                <div key={cat} data-testid={`pc-picker-category-group-${cat}`}>
                  <div className="font-mono-tech text-[0.65rem] font-bold tracking-[0.2em] uppercase text-[var(--text-secondary)] mb-2">
                    {cat} · {items.length}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {items.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  data-testid={`pc-picker-item-${p.id}`}
                  onClick={() => openProduct(p)}
                  className="border border-[var(--border)] bg-white p-3 flex gap-3 text-left hover:bg-[var(--bg)] transition-colors"
                >
                  {p.photo_url ? (
                    <img
                      src={p.photo_url}
                      alt={p.name}
                      className="w-20 h-20 object-cover border border-[var(--border)] flex-shrink-0"
                    />
                  ) : (
                    <div className="w-20 h-20 border border-dashed border-[var(--border)] flex items-center justify-center text-[10px] text-[var(--text-secondary)] flex-shrink-0">
                      Sin foto
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="font-display font-bold text-sm truncate">{p.name}</div>
                    <div className="font-mono-tech text-[10px] uppercase tracking-wider text-[var(--text-secondary)]">
                      {p.category || "Sin categoría"}
                    </div>
                    <div className="font-mono-tech text-[10px] text-[var(--text-secondary)] mt-1">
                      Tipos: {(p.types || []).length} · Cant: {(p.quantities || []).length} · Extras: {(p.extras || []).length}
                    </div>
                  </div>
                </button>
                    ))}
                  </div>
                </div>
              ))
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t-2 border-black flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="bg-black text-white px-5 py-2 font-display font-bold text-sm"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}
